import { Injectable } from '@angular/core';
import {
  FlowStartMetricsRequest,
  FlowSubmitMetricsRequest,
  IMetricsTrackingData,
  InheritanceStartMetricsRequest,
  NewAccountStartMetricsRequest,
  ProcessEndStateCodeEnum,
  ProcessPageTypeCodeEnum,
  SubmitMetricsResponse,
  SuccessfulSubmitMetricsRequest,
  TrackFailureMetricsRequest,
} from '@interfaces';
import { FundingMethodEnum } from '@interfaces/open-account/enums';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { parseStr } from '@shared/helpers';
import {
  AccountTypes,
  FlowTypes,
  GiftingDesignationTypes,
  GiftRecipientTypes,
  TransactionTypesEnum,
} from '@shared/models';
import { SubmitMetricsService } from '@shared/services/submit-metrics/submit-metrics.service';
import { selectMXMState } from '@store/selectors/mxm/mxm.selectors';
import {
  selectAccountFromIRAReceipt,
  selectAccountFromNonretirementReceipt,
  selectAccountToIRAReceipt,
  selectAccountToNonretirementReceipt,
} from '@store/selectors/receipt/inheritance/inheritance-receipt.selectors';
import { CookieService } from 'ngx-cookie-service';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectOAFromStore } from 'src/app/shared/services/open-account/oa.selectors';

import {
  submitMetricsFlowStart,
  submitMetricsFlowSubmit,
  submitMetricsOAEnd,
  submitMetricsOAStart,
  submitMetricsOASuccess,
  submitMetricUpdateProcessingId,
} from '../../actions/submit-metrics/submit-metrics.actions';
import * as fromRootStore from '../../index';
import {
  selectedClientAccountsState,
  selectGiftingState,
  selectInternalTransferState,
  selectIsIRASingleExecutor,
  selectIsSingleTrustee,
  selectTriageState,
} from '../../index';
import { getOpenAccountState } from '../../reducers';
import { selectSubmitMetricsID } from '../../selectors/open-account/open-account.selectors';
import { selectClientSegment } from '../../selectors/personal-data/personal-data.selectors';

@Injectable()
export class SubmitMetricsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private submitMetricsService: SubmitMetricsService,
    private cookieService: CookieService,
  ) {}

  submitMetricsOAStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitMetricsOAStart),
      withLatestFrom(
        this.store$.pipe(select(selectClientSegment)),
        this.store$.pipe(select(selectSubmitMetricsID)),
      ),
      switchMap(([, clientSegment, metricsID]) => {
        const submitMetricsData: InheritanceStartMetricsRequest | NewAccountStartMetricsRequest = {
          journeyName: 'change-of-ownership',
          communicationChannel: 'web',
          version: '2.0',
          eventName: clientSegment ? 'new-account-start' : 'initial-start',
          correlationId: clientSegment ? metricsID : '',
          details: {
            processPageType: ProcessPageTypeCodeEnum.OverviewPage,
            newOaUniqueStart: true,
            flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
            segmentType: clientSegment ? clientSegment : 'PR',
          },
        };

        return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
          tap((data) => {
            //if correllation we dont need to reset the cookie
            if (!metricsID) {
              data.correlationId &&
                this.cookieService.set(
                  'submitMetricsProcessingId',
                  data.correlationId,
                  undefined,
                  '/',
                  '.vanguard.com',
                  true,
                  'None',
                );
            }
          }),
          filter<SubmitMetricsResponse, IMetricsTrackingData>(
            (data): data is IMetricsTrackingData => !!data?.correlationId && !metricsID,
          ),
          map((payload) => submitMetricUpdateProcessingId({ payload })),
        );
      }),
    ),
  );

  submitMetricsOASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsOASuccess),
        withLatestFrom(
          this.store$.pipe(select(getOpenAccountState)),
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectClientSegment)),
          this.store$.pipe(select(selectOAFromStore)),
        ),
        switchMap(([, openAccountState, metricsID, clientSegment, selectedAccount]) => {
          const submitMetricsData: SuccessfulSubmitMetricsRequest = {
            journeyName: 'change-of-ownership',
            eventName: 'successful-submit',
            communicationChannel: 'web',
            correlationId: metricsID,
            version: '2.0',
            details: {
              processPageType: ProcessPageTypeCodeEnum.Confirm,
              processEndState: ProcessEndStateCodeEnum.LiteReg,
              confirmationNumber: openAccountState.openAccountResponse.confirmationNumber,
              newOaUniqueStart: false,
              paymentMethod: FundingMethodEnum.None,
              accountType: selectedAccount.selectedAccountTypeId,
              newAccount: !openAccountState.openAccountResponse.isS24Reject,
              expectedDollarAmount: '0',
              s24RuleFailId: openAccountState.openAccountResponse.isS24Reject
                ? openAccountState.openAccountResponse.failedId
                : undefined,
              flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
              segmentType: clientSegment,
            },
          };
          this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
          return this.submitMetricsService.callSubmitMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsOAEnd$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsOAEnd),
        withLatestFrom(
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectClientSegment)),
          this.store$.pipe(select(selectOAFromStore)),
        ),
        switchMap(([action, metricsID, clientSegment, selectedAccount]) => {
          const submitMetricsData: TrackFailureMetricsRequest = {
            journeyName: 'change-of-ownership',
            eventName: 'track-failure',
            correlationId: metricsID,
            communicationChannel: 'web',
            version: '2.0',
            details: {
              processPageType: action.payload,
              processEndState: ProcessEndStateCodeEnum.KickToLegacy,
              newOaUniqueStart: false,
              accountType: selectedAccount.selectedAccountTypeId,
              flowType: ProcessPageTypeCodeEnum.COOFlowTypeCode,
              segmentType: clientSegment,
            },
          };
          this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
          return this.submitMetricsService.callSubmitMetrics(submitMetricsData);
        }),
      ),
    { dispatch: false },
  );

  submitMetricsFlowStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitMetricsFlowStart),
      withLatestFrom(this.store$.pipe(select(selectTriageState))),
      switchMap(([, triageState]) => {
        const submitMetricsData: FlowStartMetricsRequest = {
          journeyName: 'change-of-ownership',
          communicationChannel: 'web',
          version: '1.0',
          eventName: 'flow-started',
          details: {
            flowName: triageState.flowType,
            transferType: triageState.flowType,
          },
        };

        return this.submitMetricsService.callSubmitMetrics(submitMetricsData).pipe(
          tap((data) => {
            data?.correlationId &&
              this.cookieService.set(
                'submitMetricsProcessingId',
                data.correlationId,
                undefined,
                '/',
                '.vanguard.com',
                true,
                'None',
              );
          }),
          filter<SubmitMetricsResponse, IMetricsTrackingData>(
            (data): data is IMetricsTrackingData => !!data?.correlationId,
          ),
          map((payload) => submitMetricUpdateProcessingId({ payload })),
        );
      }),
    ),
  );

  submitMetricsFlowSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(submitMetricsFlowSubmit),
        withLatestFrom(
          this.store$.pipe(select(selectTriageState)),
          this.store$.pipe(select(selectIsIRASingleExecutor)),
          this.store$.pipe(select(selectIsSingleTrustee)),
          this.store$.pipe(select(getOpenAccountState)),
          this.store$.pipe(select(selectGiftingState)),
          this.store$.pipe(select(selectInternalTransferState)),
          this.store$.pipe(select(selectedClientAccountsState)),
          this.store$.pipe(select(selectSubmitMetricsID)),
          this.store$.pipe(select(selectMXMState)),
          this.store$.pipe(select(selectAccountFromIRAReceipt(false))),
          this.store$.pipe(select(selectAccountFromNonretirementReceipt(false))),
          this.store$.pipe(select(selectAccountToIRAReceipt(false))),
          this.store$.pipe(select(selectAccountToNonretirementReceipt(false))),
        ),
        switchMap(
          ([
            action,
            triageState,
            isIRASingleExecutor,
            isSingleTrustee,
            openAccountState,
            giftingState,
            internalTransferState,
            selectedAccounts,
            metricsID,
            mxmState,
            accountFromIraReceipt,
            accountFromNonretirementReceipt,
            accountToIraReceipt,
            accountToNonretirementReceipt,
          ]) => {
            const transactionId = action.payload;
            const manipulatedID = Number(transactionId.replace('|', ''));
            const flowType = triageState.flowType;
            const internalGift =
              triageState.giftingDesignation === GiftingDesignationTypes.VANGUARD;
            const isNewAccount =
              internalTransferState.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
              selectedAccounts.transactionType === TransactionTypesEnum.NEW_ACCOUNT;
            let multiSigner = false;
            let transferValue = '';
            let accountFromNumber = '';
            let accountFromRegistration = '';
            let accountToNumber = '';
            let accountToRegistration = '';
            let isAppianSubmit = false;

            if (!isNaN(manipulatedID)) {
              isAppianSubmit = true;
            }

            // Sets values for the variables established above based on the flow type
            switch (flowType) {
              case FlowTypes.GIFTING:
                accountFromNumber = selectedAccounts.transferFrom.selectedAccounts[0].accountNumber;
                accountFromRegistration =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountType;
                accountToNumber = giftingState.recipient.accountNumber;
                accountToRegistration = giftingState.recipient.type;
                transferValue = giftingState.totalGiftAmount.replace(/[$,]/g, '');
                multiSigner = mxmState.signState !== null && mxmState.transferId !== null;
                break;
              case FlowTypes.MINOR:
                accountFromNumber = selectedAccounts.transferFrom.selectedAccounts[0].accountNumber;
                accountFromRegistration =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountType;
                transferValue =
                  selectedAccounts.transferFrom.selectedAccounts[0].accountData.balance;
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                  accountToRegistration = AccountTypes.INDV;
                } else {
                  accountToNumber = selectedAccounts.transferTo.selectedAccount.accountNumber;
                  accountToRegistration = selectedAccounts.transferTo.selectedAccount.accountType;
                }
                break;
              case FlowTypes.INHERITANCE:
                accountFromRegistration =
                  triageState.accountTypeInheriting === AccountTypes.IRA
                    ? accountFromIraReceipt.rows[0].values[0]
                    : accountFromNonretirementReceipt.rows[0].values[0];
                accountToRegistration =
                  triageState.accountTypeInheriting === AccountTypes.IRA
                    ? accountToIraReceipt.rows[0].values[0]
                    : accountToNonretirementReceipt.rows[0].values[0];
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                }
                if (!isNewAccount && !isIRASingleExecutor && !isSingleTrustee) {
                  accountToNumber = selectedAccounts.transferTo.selectedAccount.accountNumber;
                }
                break;
              case FlowTypes.INTERNAL_TRANSFER:
                accountFromNumber =
                  internalTransferState.transferFrom.selectedAccount.accountNumber;
                accountFromRegistration =
                  internalTransferState.transferFrom.selectedAccount.accountType;
                if (isNewAccount) {
                  accountToNumber = openAccountState.openAccountResponse.brokerageAccountNumber;
                  accountToRegistration = AccountTypes.INDV;
                } else {
                  accountToNumber = internalTransferState.transferTo.selectedAccount.accountNumber;
                  accountToRegistration = internalTransferState.triage.transferTo;
                }
                transferValue = internalTransferState.transferFrom.totalTransferValue;
                multiSigner = mxmState.signState !== null && mxmState.transferId !== null;
                break;
            }

            const submitMetricsData: FlowSubmitMetricsRequest = {
              journeyName: 'change-of-ownership',
              communicationChannel: 'web',
              correlationId: metricsID,
              version: '1.0',
              eventName: 'flow-submitted',
              details: {
                flowName: flowType,
                transferType: flowType,
                stpProcessingId: !isAppianSubmit ? transactionId : '',
                appianCaseId: isAppianSubmit ? transactionId : '',
                ...(flowType !== FlowTypes.INHERITANCE && { dollarAmount: transferValue }),
                fromRegistrationType: accountFromRegistration,
                toRegistrationType: accountToRegistration,
                fromAccountNumber: accountFromNumber,
                toAccountNumber: accountToNumber,
                isCharityOrganization: giftingState.recipient.type === GiftRecipientTypes.CHARITY,
                isInternalGift: internalGift,
                multiSigner: multiSigner,
                confirmationNumber: isNewAccount
                  ? openAccountState.openAccountResponse.confirmationNumber
                  : '',
                newAccountFlag: isNewAccount,
                costBasisOptIn: (
                  internalTransferState.transferFrom.convertAvgCostHoldings ||
                  internalTransferState.transferFrom.convertSpecIDHoldings
                ).toString(),
              },
            };
            this.cookieService.delete('submitMetricsProcessingId', '/', '.vanguard.com');
            return this.submitMetricsService.callSubmitMetrics(submitMetricsData);
          },
        ),
      ),
    { dispatch: false },
  );
}
